import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Printable from "../../Printable";
import FootballPrintOptions from "./FootballPrintOptions";
import QuickStatsPrint from "./quick-stats/QuickStatsPrint";
import BoxScore from "../../../../game-stats/components/BoxScore";
import TeamLogo from "../../../../team/components/TeamLogo";
import IndividualTeamStats from "../../../../game-stats/components/IndividualTeamStats";
import printStyles from "../styles/Print.styles";
import { useStatsContext } from "../../../../../state/stats/context";
import ParticipatingPlayers from "../../../../game-stats/components/ParticipatingPlayers";
import { usePlayersFootballContext } from "../../../../../state/players/football-context";

const FootballPrint = ({ isLoading, setIsLoading }) => {
	const classes = printStyles();
	const { state } = useStatsContext();
	const boxScore = state.boxScore() || {};
	const { awayTeamStats, homeTeamStats, homeTeam, awayTeam, event } = boxScore;
	const { sportId, eventId, startDateTime } = event || {};

	const { state: awayPlayersContextState } = usePlayersFootballContext({
		team: awayTeam
	});
	const awayParticipatingPlayers =
		awayPlayersContextState.participatingPlayers();
	const { state: homePlayersContextState } = usePlayersFootballContext({
		team: homeTeam
	});
	const homeParticipatingPlayers =
		homePlayersContextState.participatingPlayers();

	const viewType = awayPlayersContextState.view();
	const { players: awayPlayersStats } = awayTeamStats || {};
	const { players: homePlayersStats } = homeTeamStats || {};
	const [preview, setPreview] = useState(null);

	const handleSetPreview = (name = null) => {
		setPreview(name);
		setIsLoading(!!name);
	};

	const fileName = `${eventId}-${startDateTime?.split("T")?.[0]}-${sportId}.pdf`;

	if (preview === "quickStats") {
		return (
			<QuickStatsPrint
				fileName={fileName}
				setIsLoading={setIsLoading}
				onDone={() => handleSetPreview()}
			/>
		);
	}

	return (
		<>
			<FootballPrintOptions
				isLoading={isLoading}
				handleSetPreview={handleSetPreview}
			/>

			{isLoading && (
				<>
					<Printable
						showPreview={preview === "boxscore"}
						onAfterPrint={handleSetPreview}
						fileName={`Team_Stats-${fileName}`}
					>
						<BoxscoreComponent boxScore={boxScore} />
					</Printable>
					<Printable
						showPreview={preview === "away"}
						onAfterPrint={handleSetPreview}
						fileName={`Individual_${awayTeam.teamName}_Stats-${fileName}`}
					>
						<IndividualStatsComponent team={awayTeam} stats={awayPlayersStats} />
					</Printable>
					<Printable
						showPreview={preview === "home"}
						onAfterPrint={handleSetPreview}
						fileName={`Individual_${homeTeam.teamName}_Stats-${fileName}`}
					>
						<IndividualStatsComponent team={homeTeam} stats={homePlayersStats} />
					</Printable>
					<Printable
						showPreview={preview === "awayPlayers"}
						onAfterPrint={handleSetPreview}
						fileName={`${awayTeam.teamName}_Players-${fileName}`}
					>
						<ParticipatingPlayersComponent
							viewType={viewType}
							team={awayTeam}
							participatingPlayers={awayParticipatingPlayers}
						/>
					</Printable>
					<Printable
						showPreview={preview === "homePlayers"}
						onAfterPrint={handleSetPreview}
						fileName={`${homeTeam.teamName}_Players-${fileName}`}
					>
						<ParticipatingPlayersComponent
							viewType={viewType}
							team={homeTeam}
							participatingPlayers={homeParticipatingPlayers}
						/>
					</Printable>
					<Printable
						showPreview={preview === "combined"}
						onAfterPrint={handleSetPreview}
						fileName={fileName}
					>
						<Box>
							<BoxscoreComponent boxScore={boxScore} />
							<Box className={classes.newPage}>
								<IndividualStatsComponent team={awayTeam} stats={awayPlayersStats} />
							</Box>
							<Box className={classes.newPage}>
								<IndividualStatsComponent team={homeTeam} stats={homePlayersStats} />
							</Box>
							<Box className={classes.newPage}>
								<ParticipatingPlayersComponent
									viewType={viewType}
									team={awayTeam}
									participatingPlayers={awayParticipatingPlayers}
								/>
							</Box>
							<Box className={classes.newPage}>
								<ParticipatingPlayersComponent
									viewType={viewType}
									team={homeTeam}
									participatingPlayers={homeParticipatingPlayers}
								/>
							</Box>
						</Box>
					</Printable>
				</>
			)}
		</>
	);
};

const BoxscoreComponent = ({ boxScore }) => (
	<Box padding={4}>
		<BoxScore boxScore={boxScore} expanded />
	</Box>
);

const IndividualStatsComponent = ({ team, stats }) => (
	<Box px={4} pb={4}>
		<TeamLogo team={team} hideName size="large" />
		<Typography variant="h4" align="center">
			{team.teamName}
		</Typography>
		<IndividualTeamStats playerStats={stats} />
	</Box>
);

const ParticipatingPlayersComponent = ({
	team,
	participatingPlayers,
	viewType
}) => (
	<Box px={4} pb={4}>
		<TeamLogo team={team} hideName size="large" />
		<Typography variant="h4" align="center">
			{team.teamName}
		</Typography>
		<ParticipatingPlayers
			participatingPlayers={participatingPlayers}
			print
			viewType={viewType}
		/>
	</Box>
);

export default FootballPrint;
